import type { CalibrationProposal } from "@sitescene/types";

import { useState } from "react";

import { type Column, DataTable } from "@/components/data-table";
import { Button, Field, Select } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import {
	useCalibrationProposals,
	useReviewProposal,
	useRunCalibration,
} from "@/features/plausibility/use-calibration";

import { formatLogOdds, formatWhen, logOddsToPercent } from "./format";

const STATUSES = ["proposed", "accepted", "rejected"];

/**
 * What the verdicts say the tables should be. Proposals are computed across
 * every participating organization; accepting one writes the table and bumps
 * its version, which is the only way calibration data ever reaches the
 * verifier.
 */
export const Calibration = () => {
	const [status, setStatus] = useState("proposed");
	const proposals = useCalibrationProposals(status);
	const review = useReviewProposal();
	const run = useRunCalibration();
	const rows = proposals.data ?? [];

	const columns: Column<CalibrationProposal>[] = [
		{ header: "Kind", key: "kind", render: (p) => p.kind },
		{ header: "Target", key: "target", render: describeTarget },
		{
			header: "Current → proposed",
			key: "values",
			render: (p) =>
				p.proposed === null ? (
					<span className="text-tertiary">informational</span>
				) : (
					<span>
						{formatValue(p, p.current)} →{" "}
						<strong>{formatValue(p, p.proposed)}</strong>
					</span>
				),
		},
		{
			header: "Support",
			key: "support",
			render: (p) => `${p.support.positives} / ${p.support.n}`,
		},
		{ header: "Recommendation", key: "rec", render: (p) => p.recommendation },
		{
			header: "Metrics",
			key: "metrics",
			render: (p) => (
				<span className="text-secondary text-xs">
					{p.metrics
						? Object.entries(p.metrics)
								.map(([k, v]) => `${k} ${v}`)
								.join(" · ")
						: ""}
				</span>
			),
		},
		{
			header: "Computed",
			key: "computed",
			render: (p) => formatWhen(p.computedAt),
		},
		{
			header: "",
			key: "actions",
			render: (p) =>
				p.status === "proposed" && p.proposed !== null ? (
					<span className="flex gap-2">
						<Button
							disabled={review.isPending || p.recommendation === "insufficient"}
							onClick={() =>
								review.mutate({ action: "accept", proposalId: p.id })
							}
							type="button"
						>
							Accept
						</Button>
						<Button
							disabled={review.isPending}
							onClick={() =>
								review.mutate({ action: "reject", proposalId: p.id })
							}
							type="button"
							variant="danger"
						>
							Reject
						</Button>
					</span>
				) : p.reviewedAt ? (
					<span className="text-tertiary text-xs">
						{p.status} {formatWhen(p.reviewedAt)}
					</span>
				) : null,
		},
	];

	return (
		<div>
			<div className="flex flex-wrap items-end justify-between gap-3">
				<Field htmlFor="proposal-status" label="Status">
					<Select
						id="proposal-status"
						onChange={(e) => setStatus(e.target.value)}
						value={status}
					>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</Select>
				</Field>
				<div className="flex items-center gap-3">
					{run.data && (
						<span className="text-secondary text-sm">
							{run.data.samples} samples from {run.data.tenants} organization(s)
							→ {run.data.proposals} proposals
						</span>
					)}
					<Button
						disabled={run.isPending}
						onClick={() => run.mutate()}
						type="button"
						variant="secondary"
					>
						{run.isPending ? "Running…" : "Run calibration now"}
					</Button>
				</div>
			</div>
			{(review.error || run.error) && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{(review.error ?? run.error)?.message}
				</p>
			)}
			<div className="mt-4">
				<StatusMessage
					empty="No proposals. Record verdicts, then run calibration."
					error={proposals.error}
					isEmpty={!proposals.isLoading && rows.length === 0}
					isLoading={proposals.isLoading}
					loading="Loading proposals…"
				/>
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(p) => p.id} rows={rows} />
				)}
			</div>
		</div>
	);
};

const describeTarget = (p: CalibrationProposal) => {
	switch (p.kind) {
		case "anchoredModel":
			return `${p.classId ?? ""} · ${p.feature === "*" ? "all features" : (p.feature ?? "")} · real vs shadow model`;
		case "decisionQuality":
			return p.classId ?? "";
		case "lrFeature":
			return `${p.classId ?? ""} · ${p.feature ?? ""}`;
		case "lrStrength":
			return `strength: ${p.strength ?? ""}`;
		case "prior":
			return `${p.classId ?? ""} · ${p.season ?? "any season"} · ${p.hourBucket ?? "any hour"}`;
	}
};

const formatValue = (p: CalibrationProposal, value: null | number) => {
	if (value === null) return "—";
	return p.kind === "prior"
		? `${formatLogOdds(value)} (${logOddsToPercent(value)})`
		: formatLogOdds(value);
};
